<template>
    <div class="RootCause">
        <div
            v-if="attribsData"
            class="row"
        >
            <div
                v-for="idx in clampedAttribsShown"
                :key="idx"
                class="col-12 col-md-6 q-pa-sm"
            >
                <CategoricalCause
                    :usableAttribs="usableAttribs"
                    :defaultValue="attribsUsed[idx - 1]"
                    :totalEvents="attribsData.totalUnfullfilling"
                    :attrData="getCurrAttribData(attribsUsed[idx - 1])"
                    :causeIndex="idx - 1"
                    :isLoading="isLoading"
                    @changed="refreshAttributeData"
                />
            </div>
        </div>
        <div
            v-else-if="isLoading"
            class="row"
        >
            <div class="col">
                <QLinearProgress
                    indeterminate
                />
            </div>
        </div>
        <div
            v-else
            class="flex justify-center items-cemter q-ma-md"
        >
            <WText
                :size="18"
            >
                {{ $t('visualization.conformance.rootCause.noAttributes') }}
            </WText>
        </div>
    </div>
</template>
<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest,
} from '@/api'
import { filtersStorageMixin, validatorsMixin } from '@/mixins'
import CategoricalCause from './CategoricalCause.vue'

const BANNED_ATTRIBS = ['ACTIVITY', 'TRACEID', 'VariantId']

export default {
    name: 'RootCause',
    components: {
        CategoricalCause,
    },
    mixins: [filtersStorageMixin, validatorsMixin],
    props: {
        processId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        modelId: VueTypes.string,
        attribs: VueTypes.array,
        attribsShown: VueTypes.number.def(2),
    },
    data() {
        return {
            isLoading: false,
            usableAttribs: null,
            attribsUsed: null,
            defaultAttribs: null,
            attribsData: null,
        }
    },
    computed: {
        clampedAttribsShown() {
            const maxAttribs = this.attribsUsed ? this.attribsUsed.length : 0
            if (!maxAttribs) return 0
            return Math.max(1, Math.min(this.attribsShown || 1, Math.min(2, maxAttribs)))
        },
    },
    watch: {
        modelId() {
            this.setRootCauseInfo()
        },
        attribsShown() {
            this.setRootCauseInfo()
        },
        attribs: {
            handler() {
                this.setUpAttributes()
                this.setRootCauseInfo()
            },
            immediate: true,
        },
    },
    mounted() {
        this.setRootCauseInfo()
    },
    methods: {
        async fetchRootCauseInfo() {
            if (!this.modelId) return
            if (!this.attribsUsed || this.attribsUsed.length === 0) {
                this.attribsData = null
                this.isLoading = false
                return
            }
            if (this.clampedAttribsShown <= 0) {
                this.attribsData = null
                this.isLoading = false
                return
            }
            this.isLoading = true
            const { processId } = this
            const { modelId } = this
            const attributes = this.attribsUsed.slice(0, this.clampedAttribsShown)
            apiRequest(Api().conformance.rootCause({
                datasetId: processId,
                bpmnId: modelId,
                params: this.buildVisualizationParams({ attributes }),
            }))
                .then((data) => {
                    this.attribsData = data
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        setUpAttributes() {
            const attribs = Array.isArray(this.attribs) ? this.attribs : []
            this.usableAttribs = attribs.filter((x) => x.type === 'STRING' && !BANNED_ATTRIBS.includes(x.name))
            const relevantAttribs = this.usableAttribs.filter((x) => x.relevant).slice(0, 3)
            const nonRelevantAttribs = this.usableAttribs.filter((x) => !x.relevant)
            this.attribsUsed = [...relevantAttribs, ...nonRelevantAttribs].slice(0, 4)
            this.defaultAttribs = this.attribsUsed.map((x) => x.name)
            this.attribsUsed = [...this.defaultAttribs]
        },
        setRootCauseInfo() {
            if (!this.modelId) return
            this.fetchRootCauseInfo()
        },
        refreshAttributeData({ value, idx }) {
            this.attribsUsed[idx] = value.name
            if (!this.modelId) return
            this.fetchRootCauseInfo()
        },
        getCurrAttribData(attrName) {
            const [attrData] = this.attribsData.attributesCategBool.filter((x) => x.attributeName === attrName)
            return attrData
        },
    },
}
</script>
<style scoped lang="scss">
.chart {
    min-width: 300px;
    height: 400px;
    width: 100%;
}

.border-toggle {
    border: 1px solid $primary;
}
</style>
